import React, { useMemo } from 'react';
import { useRouter } from 'next/router';

import { ManagedHeading } from 'ui/components/ManagedHeading';
import { isNotNullish } from 'utils/isNotNullish';
import {
  sortCategories,
  SitemapQuery,
  buildCategoryTree,
  getNameFromUrl,
  getNavLinksFlatArray,
} from 'utils/sitemap';
import { SitemapContent } from 'types/sitemap';
import { isContentOnline } from 'utils/isContentOnline';
import { useGroqQuery, groqPreviewInput } from 'hooks/useGroqQuery';
import { PageHead } from 'ui/components/PageHead';
import { removeLeadingSlash } from 'utils/removeLeadingSlash';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { getMappedCountryCode } from 'utils/locale';

declare module 'groq/GroqOperationNames' {
  interface GroqOperationNames {
    Sitemap?: SitemapContent;
  }
}

const useSitemapQuery = () => {
  const router = useRouter();
  const [sitemap] = useGroqQuery({
    operationName: 'Sitemap',
    query: SitemapQuery,
  });

  const siteConfig = useSiteConfig();
  const filteredSitemap = useMemo(() => {
    const mainNavMobileLinks = getNavLinksFlatArray(
      sitemap?.data?.content?.mobileMainNavLinks?.children || []
    );
    const categories = sitemap?.data?.content?.categories;
    const preview = groqPreviewInput(router.query);
    const redirectsUrls =
      sitemap?.data?.content?.redirects
        ?.filter(redirect =>
          isContentOnline(
            {
              online: redirect.online,
              onlineFrom: redirect.availableFrom,
              onlineTo: redirect.availableTo,
            },
            preview
          )
        )
        .map(redirect => redirect.vanityUrl) || [];
    const rootCategory = sitemap?.data?.content?.rootCategory;
    const filteredCategories =
      sortCategories(
        categories?.filter(category => {
          if (
            redirectsUrls.includes(removeLeadingSlash(category.url)) ||
            mainNavMobileLinks.includes(removeLeadingSlash(category.url))
          ) {
            category.url = '';
            return true;
          }
          return !category.noIndex && isContentOnline(category, preview);
        })
      ) || [];

    const categoryTree = buildCategoryTree(
      filteredCategories || [],
      rootCategory
    );

    return {
      articles:
        sitemap?.data?.content?.articles?.filter(
          article =>
            isContentOnline(
              {
                onlineFrom: article.availableFrom,
                onlineTo: article.availableTo,
              },
              preview
            ) && !redirectsUrls.includes(removeLeadingSlash(article.url))
        ) || [],
      categories: categoryTree,
      supportPages:
        sitemap?.data?.content?.supportPages?.filter(
          supportPage =>
            !supportPage.noIndex &&
            isContentOnline(
              {
                onlineFrom: supportPage.availableFrom,
                onlineTo: supportPage.availableTo,
              },
              preview
            ) &&
            !redirectsUrls.includes(removeLeadingSlash(supportPage.url))
        ) || [],
      marketingPages:
        sitemap?.data?.content?.marketingPages?.filter(
          marketingPage =>
            !marketingPage?.seo?.noIndex &&
            !redirectsUrls.includes(removeLeadingSlash(marketingPage.url))
        ) || [],
      returnsPage: !siteConfig?.staticFeatures?.returnsPageDisabled &&
        !redirectsUrls.includes('returns') && {
          url: `/returns`,
          title: 'Returns',
        },
    };
  }, [sitemap, router.query, siteConfig?.staticFeatures?.returnsPageDisabled]);

  return filteredSitemap;
};

export type SitemapHeadings = string | null;

const headingElements = {
  h2: {
    tag: 'h2',
    className:
      'col-span-2 md:col-span-1 text-2xl md:text-5xl font-bold break-all',
  },
  h3: {
    tag: 'h3',
    className: 'text-base text-neutral-70 font-normal mb-2 md:mb-1',
  },
  h4: {
    tag: 'h4',
    className: 'text-base text-neutral-70 font-normal mb-2 md:mb-1',
  },
  h5: {
    tag: 'h5',
    className: 'text-base text-neutral-70 font-normal mb-2 md:mb-1',
  },
  h6: {
    tag: 'h6',
    className: 'text-base text-neutral-70 font-normal mb-2 md:mb-1',
  },
};

const renderHeading = (
  heading: SitemapHeadings,
  url: string,
  title: string
) => {
  return (
    !!heading &&
    React.createElement(
      heading,
      {
        className:
          headingElements[(heading as keyof typeof headingElements) || 'h6']
            ?.className || '',
      },
      url ? <a href={url}>{title}</a> : <>{title}</>
    )
  );
};

const renderCategories = (categories, host, locale, nest = 0) => {
  if (!categories?.length) {
    return null;
  }

  const leafCategories = categories?.filter(
    category => !category?.categories?.length
  );
  const parentCategories = categories?.filter(
    category => category?.categories?.length
  );

  const headingLevel = `h${nest + 2}`;

  if (nest === 0) {
    return parentCategories?.map(
      (category, index) =>
        category.name && (
          <React.Fragment key={index}>
            <div className="w-full bg-black h-0.5 my-8"></div>
            <div className="grid grid-cols-1 2xs:grid-cols-2 md:grid-cols-3 gap-6">
              {renderHeading(
                'h2',
                category.url
                  ? `${host}/${locale}/${removeLeadingSlash(category.url)} `
                  : '',
                category.name
              )}
              <div className="2xs:col-span-2 2xs:column-count-2 gap-4 md:gap-6">
                {category?.categories?.length > 0 &&
                  renderCategories(category.categories, host, locale, nest + 1)}
              </div>
            </div>
          </React.Fragment>
        )
    );
  }

  return (
    <>
      <ul>
        {leafCategories?.map(
          (category, index) =>
            category.url && (
              <li
                key={index}
                className="text-base text-neutral-70 font-normal mb-2 md:mb-1"
              >
                <a
                  href={
                    category.url
                      ? `${host}/${locale}/${removeLeadingSlash(category.url)} `
                      : ''
                  }
                >
                  {category.name ? category.name : getNameFromUrl(category.url)}
                </a>
              </li>
            )
        )}
      </ul>
      {parentCategories?.map((category, index) => (
        <React.Fragment key={index}>
          {renderHeading(
            headingLevel,
            category.url
              ? `${host}/${locale}/${removeLeadingSlash(category.url)} `
              : '',
            category.name || getNameFromUrl(category.url)
          )}
          {category?.categories?.length > 0 &&
            renderCategories(category.categories, host, locale, nest + 1)}
        </React.Fragment>
      ))}
    </>
  );
};

const renderList = (heading, url, items, host, locale) => {
  if (!items || items.length < 1) {
    return;
  }

  return (
    <>
      <div className="w-full bg-black h-0.5 my-8"></div>
      <div className="grid grid-cols-1 2xs:grid-cols-2 md:grid-cols-3 gap-6">
        {renderHeading(
          'h2',
          url ? `${host}/${locale}/${removeLeadingSlash(url)}` : '',
          heading || getNameFromUrl(url)
        )}
        <div className="2xs:col-span-2 2xs:column-count-2 gap-4 md:gap-6">
          <ul>
            {items?.map(
              (item, index) =>
                isNotNullish(item.url) && (
                  <li
                    key={index}
                    className="text-base text-neutral-70 font-normal mb-2 md:mb-1"
                  >
                    <a
                      href={`${host}/${locale}/${removeLeadingSlash(item.url)}`}
                    >
                      {item.title && typeof item.title === 'string'
                        ? item.title
                        : `${getNameFromUrl(item.url)}`}
                    </a>
                  </li>
                )
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

const Sitemap = () => {
  const { country: countryCode, language, host } = useSiteConfig();

  const { categories, articles, supportPages, marketingPages, returnsPage } =
    useSitemapQuery();

  const mappedCountryCode =
    getMappedCountryCode(countryCode) || countryCode.toLowerCase();
  const locale = `${mappedCountryCode}/${language}`;
  const hostUrl = host.startsWith('localhost')
    ? `http://${host}`
    : `https://${host}`;

  return (
    <>
      <PageHead title="Sitemap | Puma" />
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-16 mt-16 mb-2 md:mb-16">
        <ManagedHeading
          tag="h1"
          className="text-5xl md:text-7xl font-bold pb-4"
        >
          SITEMAP
        </ManagedHeading>
        {renderCategories(categories, hostUrl, locale)}
        {renderList('Articles', 'articles', articles, hostUrl, locale)}
        {renderList('Marketing', '', marketingPages, hostUrl, locale)}
        {renderList(
          'Support',
          '',
          [...supportPages, returnsPage],
          hostUrl,
          locale
        )}
      </div>
    </>
  );
};

export default Sitemap;
